import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi2';
import ProductCard from '@c/cards/ProductCard';
import { ProductDocument } from '@util/types/firestore/products';
import { ListingDisplay } from '@c/MasterProductsPageV2';

interface AllListingsCarouselProps {
  results: ListingDisplay[];
  brand: string;
  model: string;
}

export default function AllListingsCarousel({ results, brand, model }: AllListingsCarouselProps) {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  return (
    <section
      id="listings"
      aria-label={`All ${brand} ${model} Listings on GearFocus`}
      className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]"
    >
      <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[2rem]">
        Used {brand} {model} Listings on GearFocus
      </h2>

      <div className="relative">
        <Swiper
          modules={[Navigation]}
          spaceBetween={16}
          slidesPerView="auto"
          navigation={{
            prevEl: '.alllistings-prev-btn',
            nextEl: '.alllistings-next-btn',
          }}
          breakpoints={{
            480: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 5 },
          }}
          onInit={(swiper: SwiperType) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper: SwiperType) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
        >
          {results.map((product) => (
            <SwiperSlide key={product.id}>
              <ProductCard item={product as ProductDocument} variant="small" coverImage />
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          className={`alllistings-prev-btn flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-[#242e5f] text-white cursor-pointer transition-all hover:bg-[#C75008] absolute top-1/2 -left-[1.5rem] -translate-y-1/2 z-10 ${
            isBeginning ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          aria-label="Previous listings"
        >
          <HiArrowLeft size={20} />
        </button>

        <button
          className={`alllistings-next-btn flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-[#242e5f] text-white cursor-pointer transition-all hover:bg-[#C75008] absolute top-1/2 -right-[1.5rem] -translate-y-1/2 z-10 ${
            isEnd ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          aria-label="Next listings"
        >
          <HiArrowRight size={20} />
        </button>
      </div>
    </section>
  );
}
